import { useMemo, useRef, useState } from "react";
import { ScatterPlot, WhiteBox } from "@foodpilot/foods";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ScatterHeader } from "./ScatterHeader";
import { ScatterBox } from "./ScatterBox";
import EmptySetIcon from "src/assets/icons/empty-set.svg?react";
import { ScoreWithVariation } from "src/services/ScoreService";
import { OlapScores, OlapScoresWithPrevScores, ScoreSourceMeta } from "src/api/client-api/olapScores/olapScoresSchema";
import { getScoreScatterAxisDomain, getScoreScatterDataset, type VisualisationChoice } from "../scoreScatterData";

export type {
    IngredientSupplierVisualisationChoice,
    ProductCompanyVisualisationChoice,
    VisualisationChoice,
} from "../scoreScatterData";

export type ReferenceEntityData = {
    aggregateContributors: ScoreSourceMeta[];
    aggregateSourceMeta: ScoreSourceMeta | null;
    isInheritedFromReference: boolean;
};

export type ScoreScatterPlotProps<T extends VisualisationChoice> = {
    scores: OlapScoresWithPrevScores | undefined;
    areScoresRefetching?: boolean;

    scatteredScores: OlapScores | undefined;
    areScatteredScoresRefetching?: boolean;
    scatteredScoresDimension: string;
    getActivityDataValueByCompanyId: (companyId: number, visualisationChoiceKey: T["key"]) => number;

    scoreWithVariation: ScoreWithVariation;
    visualisationChoices: T[];
    dataLabel: string;

    onScoreDistributionClick?: (scoreId: number) => void;
    referenceScores?: Record<number, number>;
    atLeastOnePostLocked?: boolean;

    referenceEntityData?: ReferenceEntityData;
};

export const ScoreScatterPlot = <T extends VisualisationChoice>(props: ScoreScatterPlotProps<T>) => {
    const {
        scores,
        areScoresRefetching = false,
        scatteredScores,
        areScatteredScoresRefetching = false,
        scatteredScoresDimension,
        getActivityDataValueByCompanyId,
        scoreWithVariation,
        visualisationChoices,
        dataLabel,
        onScoreDistributionClick,
        referenceScores,
        atLeastOnePostLocked,
        referenceEntityData,
    } = props;

    const { t } = useTranslation();

    const [visualisationChoiceIndex, setVisualisationChoiceIndex] = useState(0);

    const scatterRef = useRef<HTMLDivElement>(null);
    const referenceScoreValue = referenceScores?.[scoreWithVariation.scoreId];

    const buttonChoices = visualisationChoices.map((choice) => ({
        id: visualisationChoices.indexOf(choice),
        label: choice.label,
        onClick: () => setVisualisationChoiceIndex(visualisationChoices.indexOf(choice)),
    }));

    const dataset =
        !areScatteredScoresRefetching ?
            getScoreScatterDataset(
                scatteredScores?.olapScores[scoreWithVariation.scoreId],
                scatteredScoresDimension,
                visualisationChoices[visualisationChoiceIndex],
                getActivityDataValueByCompanyId,
            )
        :   [];

    /**
     * Domain for ScatterPlot (xMin/xMax, yMin/yMax): 10% below the data minimum and 10% above the
     * data maximum on each axis (abscissa = activity quantity/CA, ordinate = score).
     */
    const scatterAxisDomain = useMemo(() => getScoreScatterAxisDomain(dataset), [dataset]);

    return (
        <WhiteBox sx={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
            <ScatterHeader
                scoreWithVariation={scoreWithVariation}
                visualisationChoiceIndex={visualisationChoiceIndex}
                buttonChoices={buttonChoices}
            />
            <Stack direction="row" gap="1rem" padding="1rem">
                <ScatterBox
                    title={t("en_moyenne", "On average:")}
                    scoreWithVariation={scoreWithVariation}
                    score={scores?.olapScores[scoreWithVariation.scoreId]}
                    prevScore={scores?.olapPrevScores?.[scoreWithVariation.scoreId]}
                    isRefetching={areScoresRefetching}
                    referenceScore={referenceScoreValue ?? null}
                    prevCampaignName={scores?.prevCampaignName}
                    onScoreDistributionClick={onScoreDistributionClick}
                    atLeastOnePostLocked={atLeastOnePostLocked}
                    referenceEntityData={referenceEntityData}
                />
                <Box ref={scatterRef} sx={{ flex: 2, position: "relative" }}>
                    <ScatterPlot
                        key={
                            scatterAxisDomain ?
                                `suppliers-scatter-${scatterAxisDomain.xMin}-${scatterAxisDomain.xMax}-${scatterAxisDomain.yMin}-${scatterAxisDomain.yMax}-${visualisationChoiceIndex}`
                            :   `suppliers-scatter-empty-${visualisationChoiceIndex}`
                        }
                        values={[
                            {
                                name: dataLabel,
                                data: dataset,
                            },
                        ]}
                        xMin={scatterAxisDomain?.xMin}
                        xMax={scatterAxisDomain?.xMax}
                        yMin={scatterAxisDomain?.yMin}
                        yMax={scatterAxisDomain?.yMax}
                        height="500px"
                        xLabel={
                            <Box sx={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                <Typography variant="h4">
                                    {visualisationChoices[visualisationChoiceIndex].label}
                                </Typography>
                            </Box>
                        }
                        yLabel={
                            <Box sx={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                <Typography variant="h4">{scoreWithVariation.group}</Typography>{" "}
                            </Box>
                        }
                        xUnit={visualisationChoices[visualisationChoiceIndex].unit}
                        yUnit={scoreWithVariation.unit}
                    />

                    {(areScatteredScoresRefetching || dataset.length === 0) && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                            }}
                        >
                            {areScatteredScoresRefetching ?
                                <CircularProgress />
                            :   <EmptySetIcon />}
                        </Box>
                    )}
                </Box>
            </Stack>
        </WhiteBox>
    );
};
